"use client";

import { useCallback, useEffect, useState } from "react";
import { deleteNodeProviderCredential } from "@/actions/workflows/actions";
import {
  getJotFormPinnedForms,
  getJotFormWebhookTargets,
  issueJotFormSecret,
  setJotFormPinnedForms,
} from "@/actions/workflows/jotform";
import { useNamespacedAlert } from "@/hooks/useNamespacedAlert";
import {
  JOTFORM_STORED_PROVIDER,
  type JotFormWebhookTarget,
  type JotFormWebhookTargets,
} from "@/types/jotform";
import { handledSessionExpiry, settled } from "@/utils/common/sessionExpiry";
import { normalizeFormIds } from "@/utils/integrations/jotformSetup";

/**
 * `loadError` is separate from `notIssued` on purpose: a transient failure on the
 * pin read must not render as "no credential yet", because the customer would
 * then click Generate and rotate a live secret out from under their form.
 */
export type JotFormCredentialStatus =
  | "loading"
  | "notIssued"
  | "issued"
  | "unreadable"
  | "loadError";

/**
 * Why the targets list is what it is. `requiresSecret` means nothing was
 * requested at all, and `failed` must never render as "no webhooks enabled" —
 * the customer would go and reconfigure a workflow that was already correct.
 */
export type JotFormWebhookTargetsStatus = "requiresSecret" | "loaded" | "failed";

async function readWebhookTargets(): Promise<JotFormWebhookTargets | null> {
  const result = await settled(getJotFormWebhookTargets);
  return result?.data ?? null;
}

export function useJotFormCredential() {
  const raise = useNamespacedAlert("jotformIntegration");

  const [status, setStatus] = useState<JotFormCredentialStatus>("loading");
  const [credentialId, setCredentialId] = useState<string | null>(null);
  const [pins, setPins] = useState<string[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [webhookTargets, setWebhookTargets] = useState<JotFormWebhookTarget[]>([]);
  const [webhookTargetsTruncated, setWebhookTargetsTruncated] = useState(false);
  const [webhookTargetsStatus, setWebhookTargetsStatus] =
    useState<JotFormWebhookTargetsStatus>("requiresSecret");
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [isIssuing, setIsIssuing] = useState(false);
  const [isSavingPins, setIsSavingPins] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  /** Nothing was requested, so the section must not claim the list is empty. */
  const resetWebhookTargets = useCallback(() => {
    setWebhookTargets([]);
    setWebhookTargetsTruncated(false);
    setWebhookTargetsStatus("requiresSecret");
  }, []);

  const loadWebhookTargets = useCallback(async () => {
    const targets = await readWebhookTargets();

    if (!targets) {
      setWebhookTargets([]);
      setWebhookTargetsTruncated(false);
      setWebhookTargetsStatus("failed");
      return;
    }

    setWebhookTargets(targets.targets);
    setWebhookTargetsTruncated(targets.truncated);
    setWebhookTargetsStatus("loaded");
  }, []);

  const load = useCallback(async () => {
    setStatus("loading");

    // Sequential on purpose: with no credential there is nothing to show a URL
    // for, so the targets read is skipped entirely. That costs the parallelism
    // the two reads used to have, on the rarer of the two paths.
    // A rejection arrives as `null`, which carries no status and so lands on the
    // `loadError` tail below — never on `notIssued`, which would invite the
    // customer to Generate and rotate a live secret out from under their form.
    const pinsResult = await settled(getJotFormPinnedForms);

    // The pins read is the only source of the credential id: the separate list
    // route could fail on its own and leave `status` saying "issued" while
    // Disconnect insisted no credential existed.
    setCredentialId(pinsResult?.data?.id ?? null);

    if (pinsResult?.data) {
      setPins(pinsResult.data.formIds ?? []);
      setUpdatedAt(pinsResult.data.updatedAt);
      setStatus("issued");
      await loadWebhookTargets();
      return;
    }

    resetWebhookTargets();

    if (pinsResult?.status === 404) {
      setPins([]);
      setUpdatedAt(null);
      setStatus("notIssued");
      return;
    }

    if (pinsResult?.status === 422) {
      setStatus("unreadable");
      return;
    }

    setStatus("loadError");
    raise("error", "loadError");
  }, [loadWebhookTargets, raise, resetWebhookTargets]);

  useEffect(() => {
    load();
  }, [load]);

  /** Issue or rotate — the same call. The caller owns the rotate confirmation. */
  const issue = useCallback(async (): Promise<boolean> => {
    setIsIssuing(true);
    const result = await settled(issueJotFormSecret);
    setIsIssuing(false);

    if (!result?.data) {
      raise("error", "issueError");
      return false;
    }

    setRevealedSecret(result.data.secret);
    setCredentialId(result.data.id);
    setPins(result.data.formIds ?? []);
    setUpdatedAt(result.data.updatedAt);
    setStatus("issued");
    raise("success", "secretIssued");
    // There is a credential now, so the URLs the load skipped are worth reading.
    await loadWebhookTargets();
    return true;
  }, [loadWebhookTargets, raise]);

  /** Returns the saved list on success so the editor can pick up normalization. */
  const savePins = useCallback(
    async (draft: string[]): Promise<string[] | null> => {
      const { formIds, rejected } = normalizeFormIds(draft);
      if (rejected) {
        raise("error", rejected === "tooMany" ? "pinsTooMany" : "pinsTooLong");
        return null;
      }

      setIsSavingPins(true);
      // As in `load`: a rejection is `null`, so it falls through to the generic
      // save error rather than being mistaken for a specific status.
      const result = await settled(() =>
        setJotFormPinnedForms(formIds, updatedAt ?? undefined)
      );
      setIsSavingPins(false);

      if (result?.data) {
        setPins(result.data.formIds ?? []);
        setUpdatedAt(result.data.updatedAt);
        raise("success", "pinsSaved");
        return result.data.formIds ?? [];
      }

      if (result?.status === 409) {
        // Re-read so the next attempt carries a fresh precondition. The caller
        // keeps its own draft, so nothing the user typed is thrown away. A failed
        // re-read just leaves the stale precondition in place for one more 409.
        const fresh = await settled(getJotFormPinnedForms);
        if (fresh?.data) {
          setPins(fresh.data.formIds ?? []);
          setUpdatedAt(fresh.data.updatedAt);
        }
        raise("error", "pinsConflict");
        return null;
      }

      if (result?.status === 422) {
        setStatus("unreadable");
        raise("error", "credentialUnreadable");
        return null;
      }

      if (result?.status === 404) {
        setStatus("notIssued");
        setUpdatedAt(null);
        resetWebhookTargets();
        raise("error", "notIssued");
        return null;
      }

      raise("error", "pinsSaveError");
      return null;
    },
    [raise, resetWebhookTargets, updatedAt]
  );

  const disconnect = useCallback(async (): Promise<boolean> => {
    if (!credentialId) {
      raise("error", "notIssued");
      return false;
    }

    setIsDisconnecting(true);
    try {
      // An expired session comes back as a value, not a throw — Next redacts a
      // server-action rejection, so the class cannot survive the boundary. It
      // means the delete did not happen, and the modal it raises says more than
      // a toast could.
      if (
        handledSessionExpiry(
          await deleteNodeProviderCredential(JOTFORM_STORED_PROVIDER, credentialId)
        )
      ) {
        return false;
      }
    } catch {
      raise("error", "disconnectError");
      return false;
    } finally {
      setIsDisconnecting(false);
    }

    setStatus("notIssued");
    setCredentialId(null);
    setPins([]);
    setUpdatedAt(null);
    setRevealedSecret(null);
    resetWebhookTargets();
    raise("success", "disconnected");
    return true;
  }, [credentialId, raise, resetWebhookTargets]);

  const clearRevealedSecret = useCallback(() => setRevealedSecret(null), []);

  return {
    status,
    pins,
    updatedAt,
    webhookTargets,
    webhookTargetsTruncated,
    webhookTargetsStatus,
    revealedSecret,
    isIssuing,
    isSavingPins,
    isDisconnecting,
    load,
    issue,
    savePins,
    disconnect,
    clearRevealedSecret,
    loadWebhookTargets,
  };
}
